import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthProvider";
import { Avatar } from "@/components/base/avatar/avatar";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Button } from "@/components/base/buttons/button";
import { getMyGarage, updateMyGarage, updateProfile } from "@/api/garage";
import type { UserRole, GarageDto } from "@/types/dto";
import type { SelectItemType } from "@/components/base/select/types";

const ROLE_ITEMS: (SelectItemType & { id: UserRole })[] = [
  { id: "owner", label: "Owner" },
  { id: "manager", label: "Manager" },
  { id: "mechanic", label: "Mechanic" },
  { id: "receptionist", label: "Receptionist" },
  { id: "storage_manager", label: "Storage Manager" },
];

export default function GeneralSettingsPage() {
  const { user, updateUser } = useAuth();
  const [garage, setGarage] = useState<GarageDto | null>(null);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<UserRole>("owner");

  // Garage fields
  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [brandName, setBrandName] = useState("");

  const fetchGarage = useCallback(async () => {
    try {
      const data = await getMyGarage();
      setGarage(data);
      setCompanyName(data.name ?? "");
      setBrandName(data.brandName ?? data.name ?? "");
      setVatNumber(data.vatNumber ?? "");
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchGarage();
  }, [fetchGarage]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setRole(user.role);
    }
  }, [user]);

  if (!user) return null;

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
  const canChangeRole = user.role === "admin";

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update profile
      const updatedUser = await updateProfile({
        firstName,
        lastName,
        ...(canChangeRole ? { role } : {}),
      });
      updateUser(updatedUser);

      // Update garage
      if (garage) {
        await updateMyGarage({
          name: companyName,
          brandName,
          vatNumber,
        });
      }

      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-md font-semibold text-text-primary">General</h2>
          <p className="mt-1 text-sm text-text-tertiary">
            Your profile and garage information.
          </p>
        </div>
        <Button
          size="md"
          color="primary"
          isLoading={saving}
          isDisabled={saving}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>

      {/* Profile */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-6">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Profile</h3>
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <Avatar size="xl" initials={initials} />

          <div className="flex-1 min-w-0 w-full space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="First name"
                size="sm"
                value={firstName}
                onChange={setFirstName}
              />
              <Input
                label="Last name"
                size="sm"
                value={lastName}
                onChange={setLastName}
              />
            </div>

            <Input
              label="Email"
              size="sm"
              value={user.email}
              isDisabled
            />

            {canChangeRole ? (
              <Select
                label="Role"
                size="sm"
                items={ROLE_ITEMS}
                selectedKey={role}
                onSelectionChange={(key) => setRole(key as UserRole)}
              >
                {(item) => (
                  <Select.Item key={item.id} id={item.id} label={item.label} />
                )}
              </Select>
            ) : (
              <Input
                label="Role"
                size="sm"
                value={ROLE_ITEMS.find((r) => r.id === user.role)?.label ?? user.role}
                isDisabled
              />
            )}
          </div>
        </div>
      </div>

      {/* Garage */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-6">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Garage</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Company name"
              size="sm"
              value={companyName}
              onChange={setCompanyName}
              hint="The legal name of your business."
            />
            <Input
              label="VAT No."
              size="sm"
              value={vatNumber}
              onChange={setVatNumber}
              hint="Your VAT registration number."
            />
          </div>
          <Input
            label="Garage name"
            size="sm"
            value={brandName}
            onChange={setBrandName}
            hint="Your brand name — shown in the logo and across the app."
          />
        </div>
      </div>
    </div>
  );
}
